import React from "react";
import { UiSchema, WidgetProps } from "@rjsf/utils";
import CustomObjectTemplate from "./CustomObjectTemplate";
import CustomCheckbox from "./CustomCheckbox";
import CustomRangeWidget from "./CustomRangeWidget";
import { CustomArrayField } from "./CustomArrayField";
import { AttentionIcon, CopyToClipboard, HideString, ShowString } from "../ProjectIcons";
import CustomFileField from "./CustomFileField";
import { toast } from "react-toastify";
import i18next from "../../../components/multilanguage/i18n";

const PasswordWidget = (props: WidgetProps) => {
    const {
        id,
        placeholder,
        value,
        required,
        disabled,
        readonly,
        autofocus,
        options = {},
        onChange,
        onBlur,
        onFocus
    } = props;
    const { isVisible, togglePassword, copyValue } = options as any;
    const inputType = isVisible ? 'text' : 'password';

    const handleCopy = () => {
        if (copyValue) {
            copyValue();
        }
    };

    return (
        <div className="flex flex-row items-stretch justify-between gap-2">
            <div className="relative flex-1">
                <input
                    id={id}
                    type={inputType}
                    placeholder={placeholder || ''}
                    value={value || ''}
                    required={required}
                    disabled={disabled}
                    readOnly={readonly}
                    autoFocus={autofocus}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={onBlur ? (event) => onBlur(id, event.target.value) : undefined}
                    onFocus={onFocus ? (event) => onFocus(id, event.target.value) : undefined}
                    className="w-full rounded form-control border border-neutral-gray-400 focus:border-dark-green focus:ring-dark-green focus:ring-1 pr-12"
                />
                {togglePassword && (
                    <button
                        type="button"
                        onClick={togglePassword}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-off-black focus:outline-none"
                        aria-label="Toggle password visibility"
                    >
                        {isVisible ? <ShowString /> : <HideString />}
                    </button>
                )}
            </div>
            {copyValue && (
                <button
                    type="button"
                    onClick={handleCopy}
                    className="flex items-center justify-center rounded border border-neutral-gray-300 px-3"
                    aria-label="Copy password to clipboard"
                >
                    <CopyToClipboard />
                </button>
            )}
        </div>
    );
};

const customWidgets: any = {
    range: CustomRangeWidget,
    boolean: (props: any) => {
        return <CustomCheckbox {...props} formData={props.formData} />;
    },
    object: CustomObjectTemplate,
    array: CustomArrayField,
    file: CustomFileField
};

// Widget types that render space-hungry controls (e.g. the collar-registration file
// table used by Vectronic). These are flagged full-width so they span the whole form
// row instead of the default 50/50 label/field split. Extend this list for future
// integrations that need a configurable control with more room.
const FULL_WIDTH_WIDGET_TYPES = ['file'];

// CustomArrayField only knows how to render flat items: strings, enums and
// fixed-choice option lists. Arrays of objects (Pydantic models arrive as
// definitions + $ref) must go through rjsf's own ArrayField, which resolves
// the refs and renders the nested fields with their widgets and descriptions.
// CustomArrayField would collapse them into bare text inputs, and its
// side-by-side title/items layout halves the available width at every
// nesting level.
const SCALAR_ITEM_TYPES = ['string', 'number', 'integer', 'boolean'];
const hasScalarItems = (propertySchema: any): boolean => {
    const items = propertySchema?.items;
    if (!items || Array.isArray(items)) {
        return false;
    }
    return SCALAR_ITEM_TYPES.includes(items.type) || Array.isArray(items.enum);
};

const processFields = (
    userIsAdmin: boolean,
    schema: any,
    uiSchema: UiSchema,
    errors: any,
    propertyName: string,
    visiblePasswords: Record<string, boolean>,
    togglePassword: (fieldName: string) => void,
    formData: any) => {
    // Process fields here
    const isRequired = schema.required && schema.required.includes(propertyName);
    const title = schema.properties[propertyName].title || (propertyName.charAt(0).toUpperCase() + propertyName.slice(1)) || '';
    // Initialize the property in uiSchema if it doesn't exist
    uiSchema[propertyName] = uiSchema[propertyName] || {};

    // Set the title with required indicator
    uiSchema[propertyName]['ui:title'] = (
        <div className="flex items-baseline">
            <span className="mr-1">{title}</span>
            {isRequired && <span className="mr-1"> * </span>}
        </div>
    );    // Handle errors
    if (errors && errors[propertyName]) {
        const errorMessage = errors[propertyName];
        uiSchema[propertyName]['ui:options'] = {
            ...uiSchema[propertyName]['ui:options'],
            className: 'border border-orange-600 focus:border-orange-600 focus:ring-orange-600'
        };
        uiSchema[propertyName]['ui:help'] = (
            <div className="text-orange-600 py-1 rounded flex flex-row items-center">
                <AttentionIcon /> <p className="ml-1 mt-0.5">{errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)}</p>
            </div>
        );
    } else {
        uiSchema[propertyName]['ui:help'] = null; // Ensure ui:help is cleared when there are no errors
    }

    // Handle custom widgets and default widgets
    const widgetType = uiSchema[propertyName]['ui:widget'];
    const propertyType = schema.properties[propertyName].type;
    const propertyFormat = schema.properties[propertyName].format;

    // Always use PasswordWidget for password fields if userIsAdmin, even if ui:widget is set to 'password'
    if (propertyFormat === 'password' && userIsAdmin) {
        const existingHelp = uiSchema[propertyName]['ui:help'];
        const isVisible = visiblePasswords[propertyName] || false;
        const copyValue = () => {
            const value = formData?.[propertyName] || '';
            navigator.clipboard.writeText(value);
            toast.info(`${title} ${i18next.t('copied to clipboard')}`, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 2000
            });
        };
        uiSchema[propertyName]['ui:widget'] = PasswordWidget;
        uiSchema[propertyName]['ui:options'] = {
            ...uiSchema[propertyName]['ui:options'],
            isVisible,
            togglePassword: () => togglePassword(propertyName),
            copyValue
        };
        uiSchema[propertyName]['ui:help'] = existingHelp;
    } else if (["fire_lookback_days", "integrated_alerts_lookback_days"].includes(propertyName)) {
        uiSchema[propertyName]['ui:widget'] = CustomRangeWidget;
    } else if ((propertyType === 'integer' || propertyType === 'number') && widgetType === 'range') {
        uiSchema[propertyName]['ui:widget'] = CustomRangeWidget;
    } else if (widgetType === 'checkboxes' && propertyType === 'array') {
        // Multi-select checkbox arrays render through CustomArrayField (styled
        // switches, two-column layout) instead of rjsf's stock CheckboxesWidget,
        // which outputs unstyled native checkboxes in a single column.
        // Clone before deleting: the nested per-property object may still be
        // shared by reference with the provider type held in React state.
        uiSchema[propertyName] = { ...uiSchema[propertyName] };
        delete uiSchema[propertyName]['ui:widget'];
        uiSchema[propertyName]['ui:field'] = CustomArrayField;
    } else if (widgetType && customWidgets[widgetType]) {
        // Scenario 1: ui_schema contains a widget specification and a custom widget is available
        uiSchema[propertyName]['ui:widget'] = customWidgets[widgetType];
        // Flag space-hungry widgets so the field template renders them full-width.
        // Checked here (before the string widgetType is swapped for the component).
        if (FULL_WIDTH_WIDGET_TYPES.includes(widgetType)) {
            uiSchema[propertyName]['ui:options'] = {
                ...uiSchema[propertyName]['ui:options'],
                fullWidth: true,
            };
        }
    } else if (widgetType) {
        // Scenario 2: ui_schema contains a widget specification but no custom widget is available
        uiSchema[propertyName]['ui:widget'] = widgetType;
    } else if (propertyFormat === 'password') {
        // Fallback for non-admins
        const existingHelp = uiSchema[propertyName]['ui:help'];
        const isVisible = visiblePasswords[propertyName] || false;
        uiSchema[propertyName]['ui:widget'] = 'password';
        uiSchema[propertyName]['ui:options'] = {
            ...uiSchema[propertyName]['ui:options'],
            inputType: isVisible ? 'text' : 'password',
        };
        uiSchema[propertyName]['ui:help'] = existingHelp;
    } else if (customWidgets[propertyType]
        && (propertyType !== 'array' || hasScalarItems(schema.properties[propertyName]))) {
        // Scenario 3: ui_schema does not have a widget specification, but a custom widget is available for the property type
        uiSchema[propertyName]['ui:field'] = customWidgets[propertyType];
    }

    // If the field has only one enum option, disable it (value is auto-selected in SchemaForm)
    if (schema.properties[propertyName].enum?.length === 1) {
        uiSchema[propertyName]['ui:disabled'] = true;
    }

    // Set the title with required indicator
    uiSchema[propertyName]['ui:title'] = (
        <div className="flex items-baseline">
            <span className="mr-1">{title}</span>
            {isRequired && <span className="mr-1"> * </span>}
        </div>
    );
}

// Function to process conditional fields dynamically
const processConditionals = (
    userIsAdmin: boolean,
    schema: any,
    propertyName: string,
    formData: any,
    uiSchema: UiSchema,
    errors: any,
    visiblePasswords: Record<string, boolean>,
    togglePassword: (fieldName: string) => void,
    providedUiSchema: UiSchema
) => {
    if (!schema.if) return;

    const conditionMet = Object.entries(schema.if.properties || {}).every(([key, condition]) =>
        formData?.[key] === (condition as { const: any }).const
    );

    const processProperties = (conditionalSchema: any) => {
        Object.keys(conditionalSchema?.properties || {}).forEach((property) => {
            if (conditionalSchema.properties[property].type === "boolean" && formData?.[property] === undefined) {
                formData[property] = conditionalSchema.properties[property].default ?? false;
            }
            // Merge top-level UI schema for conditional fields
            if (uiSchema[property] === undefined && property in uiSchema) {
                uiSchema[property] = { ...uiSchema[property] };
            }
            // If top-level UI schema has widget, apply it
            if (uiSchema[property] === undefined && property in providedUiSchema) {
                uiSchema[property] = { ...providedUiSchema[property] };
            }
            processFields(userIsAdmin, conditionalSchema, uiSchema, errors, property, visiblePasswords, togglePassword, formData);
            // Error handling logic
            if (errors && errors[property]) {
                const errorMessage = errors[property];
                uiSchema[property]['ui:options'] = {
                    ...uiSchema[property]['ui:options'],
                    className: 'border border-orange-600 focus:border-orange-600 focus:ring-orange-600'
                };
                uiSchema[property]['ui:help'] = (
                    <div className="text-orange-600 py-1 rounded flex flex-row items-center">
                        <AttentionIcon /> <p className="ml-1 mt-0.5">{errorMessage}</p>
                    </div>
                );
            } else {
                uiSchema[propertyName]['ui:help'] = null
            }
        });
    };

    if (conditionMet) {
        processProperties(schema.then);
    } else {
        processProperties(schema.else);
    }
};

export const generateUISchema = (
    userIsAdmin: boolean,
    schema: any,
    providedUiSchema: UiSchema = {},
    errors: any,
    visiblePasswords: Record<string, boolean>,
    togglePassword: (fieldName: string) => void,
    formData: any
) => {
    if (schema && schema.properties) {
        const uiSchema: UiSchema = { ...providedUiSchema };

        // Set overall form layout for horizontal format
        uiSchema['ui:classNames'] = 'horizontal-form';

        if (schema.title) {
            uiSchema['ui:title'] = '';
        }

        Object.keys(schema.properties).forEach((propertyName) => {
            processFields(userIsAdmin, schema, uiSchema, errors, propertyName, visiblePasswords, togglePassword, formData);
            // Process conditional properties
            processConditionals(userIsAdmin, schema, propertyName, formData, uiSchema, errors, visiblePasswords, togglePassword, providedUiSchema);
        });

        // Preserve ui:order if it exists in the providedUiSchema
        if (providedUiSchema['ui:order']) {
            uiSchema['ui:order'] = providedUiSchema['ui:order'];
        }
        return uiSchema;
    }

    return {};
};